import { z } from 'zod';
import { LLM_TASKS } from './ai-ops.js';
import { cursorPageQuerySchema } from './pagination.js';
/**
 * The Gyms screen: every gym on Steado for Gyms. Read only, except that an
 * admin creates a gym and its owner (and sends the owner's invite again), and
 * sets a gym's member limit and AI limits. Pricing is agreed with Steado
 * outside the app: there are no seats, quotes or trial dates here.
 *
 * **Counts, never people.** A gym's members are Steado app users, and the
 * Users screen already lists them (filter `gym`), so nothing here names a
 * member. No health data, no logs, no message or change-request text, no
 * draft documents: only how many. The owner's and the trainers' contact
 * details are here, for support, as phone numbers are on Users.
 *
 * **The join code is absent on purpose.** Anyone holding it can send the gym
 * join requests, and support never needs it.
 *
 * **Not behind the `gym_api` switch.** The switch hides gyms from the apps;
 * operators still need to see what the gym tables hold while it is off.
 */
// --- Vocabularies -----------------------------------------------------------
/** Mirrors Prisma's `GymSetupStep`: how far the owner got through setup. */
export const GYM_SETUP_STEPS = ['gym', 'trainers', 'share', 'done'];
/** Mirrors Prisma's `GymStaffStatus`. */
export const GYM_STAFF_STATUSES = ['invited', 'active', 'removed', 'revoked', 'declined'];
/** Mirrors Prisma's `GymMemberStatus`. */
export const GYM_MEMBER_STATUSES = ['active', 'ended'];
/** Mirrors Prisma's `GymCoachingEndedBy`. */
export const GYM_COACHING_ENDED_BY = ['owner', 'member'];
/**
 * Where the owner's account stands. Steado creates every owner from the
 * console and emails an invite: `invited` until they accept it and set a
 * password, `active` after (and for every owner who signed up before invites).
 */
export const GYM_OWNER_STATUSES = ['invited', 'active'];
export const GYM_SETUP_FILTERS = ['done', 'not_done'];
/**
 * The window behind every `…InWindow` count: a rolling 7 days back from the
 * request, not calendar days, so it means the same in every gym's zone.
 */
export const GYM_ACTIVITY_WINDOW_DAYS = 7;
// --- Shared pieces ----------------------------------------------------------
/**
 * A gym's member limit: how many members it may have (active memberships,
 * with or without a trainer; trainers never count). Steado sets it here; a new
 * gym gets the default. At the limit, members can still ask to join, but the
 * owner cannot approve them. The API's `GYM_DEFAULT_MEMBER_LIMIT`
 * (`src/config/constants.ts`) is the same default, and a unit test holds them equal.
 */
export const GYM_MEMBER_LIMIT_DEFAULT = 25;
export const GYM_MEMBER_LIMIT_MAX = 10_000;
const memberLimitSchema = z
    .number()
    .int('Must be a whole number.')
    .min(1, 'Must be at least 1.')
    .max(GYM_MEMBER_LIMIT_MAX, `Must be at most ${GYM_MEMBER_LIMIT_MAX.toLocaleString('en-IN')}.`);
/**
 * What a gym serves, counted the way the owner app counts it. The list and
 * the detail compute these with one query, so the two never disagree.
 *
 * A member whose Steado account is deleted drops out of every count (the
 * account's gym rows go with it), so these describe what is on record.
 */
export const gymCountsSchema = z.object({
    /** People coached now: distinct users with an active membership. Exactly the users `/users?gym=<id>` lists. */
    coached: z.number().int(),
    /** Distinct users with any membership, past or present. Someone who left and rejoined counts once. */
    peopleEver: z.number().int(),
    /** `peopleEver - coached`: people whose coaching ended. */
    left: z.number().int(),
    /** Coached, but no longer sharing their profile and logs with the trainer. */
    notSharing: z.number().int(),
    /** Active members with an active trainer and no first plan yet. */
    waitingFirstPlan: z.number().int(),
    /** Active members with no trainer, or whose trainer is no longer active. */
    needsTrainer: z.number().int(),
    /** Coached members who logged a meal or finished a workout in the window. */
    loggedInWindow: z.number().int(),
    joinRequestsWaiting: z.number().int(),
    trainersActive: z.number().int(),
    trainersInvited: z.number().int(),
    /** Plans trainers sent, new weeks and revisions, up to now. */
    plansPublished: z.number().int(),
    plansPublishedInWindow: z.number().int(),
    /** Of `plansPublished`, revisions of a plan the member already had. */
    revisionsPublished: z.number().int(),
    /**
     * AI drafts made, as the AI allowance counts them (a failed run is not one).
     * Only those still on record: a trainer's unpublished drafts go when their
     * access ends.
     */
    aiDrafts: z.number().int(),
    aiDraftsInWindow: z.number().int(),
    /** Change requests from active members that a trainer has not handled yet. */
    changeRequestsOpen: z.number().int(),
    /** Trainer messages to members in the window. */
    messagesInWindow: z.number().int(),
});
/**
 * The owner's account status and, while invited, the newest invite: when it
 * was sent and when its link stops working (it may already have). Both null
 * once the owner is active, and for an invited owner who has never had one.
 */
const ownerInviteFields = {
    status: z.enum(GYM_OWNER_STATUSES),
    inviteSentAt: z.string().nullable(),
    inviteExpiresAt: z.string().nullable(),
};
// --- The list ---------------------------------------------------------------
export const adminGymListQuerySchema = cursorPageQuerySchema.extend({
    /** Gym name, owner name or owner email; matched as typed, case-insensitively. */
    search: z.string().trim().max(120).optional(),
    setup: z.enum(GYM_SETUP_FILTERS).optional(),
});
export const adminGymListItemSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    timeZone: z.string(),
    owner: z.object({
        id: z.string().uuid(),
        displayName: z.string(),
        email: z.string(),
        emailConfirmed: z.boolean(),
        /** When the owner app last refreshed its session; null if never signed in after sign-up. */
        lastSeenAt: z.string().nullable(),
        ...ownerInviteFields,
    }),
    setupStep: z.enum(GYM_SETUP_STEPS),
    /** Members now (active memberships), as the member limit counts them. */
    memberCount: z.number().int(),
    memberLimit: z.number().int(),
    createdAt: z.string(),
    counts: gymCountsSchema,
    /**
     * The gym's AI tokens this month are at or past 80% of its monthly cap
     * (the effective one, override or default). The numbers are on
     * `adminGymAiLimitsSchema`.
     */
    aiNearLimit: z.boolean(),
});
export const adminGymParamsSchema = z.object({ id: z.string().uuid() });
// --- The detail -------------------------------------------------------------
export const gymTrainerSchema = z.object({
    staffId: z.string().uuid(),
    /** Their Steado account; null while the invite is unaccepted. */
    userId: z.string().uuid().nullable(),
    displayName: z.string(),
    phone: z.string(),
    status: z.enum(GYM_STAFF_STATUSES),
    invitedAt: z.string().nullable(),
    inviteExpiresAt: z.string().nullable(),
    acceptedAt: z.string().nullable(),
    removedAt: z.string().nullable(),
    /** Active members assigned to them now. */
    activeMembers: z.number().int(),
    /** Plans they sent at this gym, up to now. */
    plansPublished: z.number().int(),
});
/** One audit row about the gym or its owner account: what and when, never its metadata. */
export const gymActivityEntrySchema = z.object({
    id: z.string().uuid(),
    action: z.string(),
    entity: z.string(),
    createdAt: z.string(),
});
/** How many audit rows the detail carries; Admin activity has the full trail. */
export const GYM_ACTIVITY_LIMIT = 10;
export const adminGymDetailSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    timeZone: z.string(),
    /** The zone as the owner app names it ("India (IST)"). */
    timeZoneLabel: z.string(),
    setupStep: z.enum(GYM_SETUP_STEPS),
    createdAt: z.string(),
    owner: z.object({
        id: z.string().uuid(),
        displayName: z.string(),
        email: z.string(),
        phone: z.string().nullable(),
        emailConfirmedAt: z.string().nullable(),
        /** When the account was created: by the owner's sign-up, or by Steado's console. */
        signedUpAt: z.string(),
        termsVersion: z.string().nullable(),
        lastSeenAt: z.string().nullable(),
        ...ownerInviteFields,
        /** When the owner accepted the terms (with the invite, or at sign-up); null while invited. */
        activeSince: z.string().nullable(),
    }),
    /** Members now (active memberships), as the member limit counts them. */
    memberCount: z.number().int(),
    memberLimit: z.number().int(),
    counts: gymCountsSchema,
    /** Every staff row, active first. */
    trainers: z.array(gymTrainerSchema),
    /** Newest first, at most `GYM_ACTIVITY_LIMIT`; sign-ins and sign-outs left out. */
    activity: z.array(gymActivityEntrySchema),
});
// --- Creating a gym -----------------------------------------------------------
/** Characters are code points, as the owner app counts them: ten emoji are ten characters. */
const codePoints = (value) => Array.from(value).length;
const between = (min, max) => (value) => codePoints(value) >= min && codePoints(value) <= max;
/**
 * A gym and its owner, created by Steado (owners cannot sign themselves up).
 * The owner is emailed an invite valid for 7 days; they set a password by
 * accepting it. `gymName` follows the rule `PATCH /gyms/:gymId` applies
 * (`gymNameSchema`, `src/modules/gym-owners/gym-owners.schema.ts`). `timeZone`
 * is one of `GET /time-zones` (checked by the API, which owns the list) and
 * defaults to Asia/Kolkata.
 */
export const adminGymCreateSchema = z.object({
    gymName: z.string().trim().refine(between(1, 80), 'Must be 1 to 80 characters.'),
    ownerName: z.string().trim().refine(between(1, 80), 'Must be 1 to 80 characters.'),
    ownerEmail: z
        .string()
        .trim()
        .max(254, 'Enter a valid email address.')
        .regex(/^[^@\s]+@[^@\s]+\.[^@\s]+$/, 'Enter a valid email address.'),
    ownerPhone: z
        .string()
        .trim()
        .regex(/^\+[1-9]\d{1,14}$/, 'Must be a valid phone number in E.164 format.')
        .optional(),
    timeZone: z.string().trim().min(1).max(64).optional(),
    /** How many members the gym may have; `GYM_MEMBER_LIMIT_DEFAULT` when left out. */
    memberLimit: memberLimitSchema.optional(),
});
export const adminGymCreatedSchema = z.object({
    gymId: z.string().uuid(),
    ownerId: z.string().uuid(),
    ownerEmail: z.string(),
    inviteExpiresAt: z.string(),
});
/** A fresh invite sent to an owner who has not accepted one yet. */
export const adminGymOwnerInviteSchema = z.object({ inviteExpiresAt: z.string() });
/**
 * A new member limit. Lower than the members the gym has now is refused
 * (409 MEMBER_LIMIT_BELOW_COUNT): end someone's coaching first.
 */
export const adminGymMemberLimitUpdateSchema = z.object({ memberLimit: memberLimitSchema }).strict();
export const adminGymMemberLimitSchema = z.object({
    memberLimit: z.number().int(),
    memberCount: z.number().int(),
});
/** A time zone a gym may be in, as `GET /time-zones` lists them (India first). */
export const gymTimeZoneOptionSchema = z.object({ id: z.string(), label: z.string() });
// --- AI limits --------------------------------------------------------------
/**
 * The gym AI's three caps. Each has an environment default and an optional
 * per-gym override that an admin sets here (null takes the default).
 *
 * - `monthlyTokens`: input + output tokens of every gym AI call (Ask, the
 *   insight writer, AI drafts) in the gym's calendar month.
 * - `askDailyTokens`: Ask tokens one trainer, or the owner, may use in the
 *   gym's day.
 * - `draftsPerMemberWeek`: AI drafts one member may have started in any
 *   rolling seven days. 0 switches AI drafts off for the gym.
 */
export const GYM_AI_CAP_KEYS = ['monthlyTokens', 'askDailyTokens', 'draftsPerMemberWeek'];
/** A cap: the environment's default, the gym's own override, and what is enforced. */
export const adminGymAiCapSchema = z.object({
    /** The environment's default. */
    default: z.number().int(),
    /** This gym's own cap; null when it takes the default. */
    override: z.number().int().nullable(),
    /** What is enforced: the override, else the default. */
    effective: z.number().int(),
});
/** One person's Ask tokens today. */
export const adminGymAiAskerSchema = z.object({
    name: z.string(),
    role: z.enum(['trainer', 'owner']),
    tokens: z.number().int(),
});
/** What one task's calls cost this month. `usd` is null when no call of the task reported a price. */
export const adminGymAiTaskSpendSchema = z.object({
    task: z.enum(LLM_TASKS),
    usd: z.number().nullable(),
    tokens: z.number().int(),
    calls: z.number().int(),
});
export const adminGymAiLimitsSchema = z.object({
    gymId: z.string().uuid(),
    /**
     * The gym's calendar month, `YYYY-MM-DD` in its own zone: `from` is its first
     * day, `to` its last day, `resetsOn` the first day of the next month, when
     * the monthly count starts again.
     */
    month: z.object({ from: z.string(), to: z.string(), resetsOn: z.string() }),
    monthlyTokens: adminGymAiCapSchema.extend({
        /** Tokens the gym's AI calls used this month. */
        used: z.number().int(),
        /** Used is at or past 80% of the effective cap. */
        nearLimit: z.boolean(),
        /** Used has reached the effective cap: Ask and new AI drafts are refused until `month.resetsOn`. */
        reached: z.boolean(),
    }),
    askDailyTokens: adminGymAiCapSchema.extend({
        /** Today's heaviest askers (the gym's day), most tokens first, at most five. */
        topToday: z.array(adminGymAiAskerSchema),
        /** The heaviest asker today is at or past 80% of the effective cap. */
        nearLimit: z.boolean(),
    }),
    draftsPerMemberWeek: adminGymAiCapSchema,
    /** What the gym's AI calls cost this month. */
    spendThisMonth: z.object({
        /** Known spend in USD; null when no call this month reported a price. */
        totalUsd: z.number().nullable(),
        /** Calls this month with no reported price: `totalUsd` is a floor when above 0. */
        costUnknownCalls: z.number().int(),
        byTask: z.array(adminGymAiTaskSpendSchema),
    }),
});
/**
 * Sets or clears overrides. A key left out is unchanged, a number sets the
 * gym's own cap, `null` clears it back to the default. At least one key.
 */
export const adminGymAiLimitsUpdateSchema = z
    .object({
    monthlyTokens: z.number().int().min(1).max(1_000_000_000).nullable().optional(),
    askDailyTokens: z.number().int().min(1).max(100_000_000).nullable().optional(),
    draftsPerMemberWeek: z.number().int().min(0).max(50).nullable().optional(),
})
    .strict()
    .refine((body) => GYM_AI_CAP_KEYS.some((key) => body[key] !== undefined), {
    message: 'Set or clear at least one limit.',
});
// --- Account deletion requests ------------------------------------------------
/**
 * "Delete account" in the Steado for Gyms app: a trainer or a gym owner asked
 * Steado to delete their account, and a person completes it within 30 days.
 * The team is emailed when one is filed; this list is where the open ones are
 * worked and marked done. Erasing a user marks their open request done.
 */
/** Mirrors Prisma's `GymAccountDeletionKind`: a trainer (a Steado user) or an owner. */
export const GYM_ACCOUNT_DELETION_KINDS = ['user', 'owner'];
/** Mirrors Prisma's `GymAccountDeletionStatus`. */
export const GYM_ACCOUNT_DELETION_STATUSES = ['open', 'done'];
export const adminGymDeletionRequestSchema = z.object({
    id: z.string().uuid(),
    kind: z.enum(GYM_ACCOUNT_DELETION_KINDS),
    status: z.enum(GYM_ACCOUNT_DELETION_STATUSES),
    /** What the person typed, if anything; cleared once done. */
    reason: z.string().nullable(),
    requestedAt: z.string(),
    doneAt: z.string().nullable(),
    /**
     * Who asked, while the account exists: a trainer's user id and phone, or an
     * owner's account id and email. Null once the account is deleted.
     */
    person: z
        .object({
        name: z.string(),
        contact: z.string(),
        userId: z.string().uuid().nullable(),
        ownerId: z.string().uuid().nullable(),
    })
        .nullable(),
    /** The gyms they train at (active) or own. */
    gyms: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
});
export const adminGymDeletionListQuerySchema = z.object({
    status: z.enum(GYM_ACCOUNT_DELETION_STATUSES).default('open'),
});
export const adminGymDeletionParamsSchema = z.object({ id: z.string().uuid() });
//# sourceMappingURL=gyms.js.map